import { getMainPicture } from 'affiny-core/redux/selectors/picturesSelectors';
import { produce } from 'immer';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { EPhotoRevealStatus } from '../api/models/member/member.types';
import { EBlacklistActionType, TBlacklistAction } from '../blacklist/actions';
import { EFavoritesActionType, IFavoritesAction } from '../favorites/actions';
import { initialRootState } from '../initialRootState';
import { EMatchesActionType, TMatchesActions } from '../matches/actions';
import {
  EPhotoRequestsActionType,
  TPhotoRequestsAction,
} from '../photoRequests/actions';
import {
  EPhotoRevealsActionType,
  TPhotoRevealsActions,
} from '../photoReveals/actions';
import {
  EPicturesActionType,
  IGetPicturesSuccessAction,
} from '../pictures/actions';
import { EScoreActionType, IGetScoreSuccessAction } from '../scores/actions';
import {
  EThreadActionType,
  EThreadsActionType,
  TThreadAction,
  TThreadsAction,
} from '../threads/actions';
import { EVisitsActionType, IVisitsAction } from '../visits/actions';
import { EMembersProfileActionType, IMembersAction } from './actions';
import { IMembersEntries } from './types';

export interface IMembersState {
  entries: IMembersEntries;
}

export const membersReducer = (
  currentState: IMembersState = initialRootState.members,
  action:
    | IMembersAction
    | TThreadAction
    | TThreadsAction
    | IGetScoreSuccessAction
    | TBlacklistAction
    | IFavoritesAction
    | IVisitsAction
    | TMatchesActions
    | TPhotoRevealsActions
    | TPhotoRequestsAction
    | TPhotoRequestsAction
    | IGetPicturesSuccessAction
): IMembersState =>
  produce(currentState, draft => {
    switch (action.type) {
      case EPicturesActionType.GET_SUCCESS:
        const { pictures } = action.payload.entities;
        const mainPicture = pictures && getMainPicture(Object.values(pictures));
        const pictureAccountId = mainPicture?.attributes.aboid;
        const memberWithPicture = mainPicture &&
          pictureAccountId && {
            [pictureAccountId]: {
              attributes: {
                mainPicture: mainPicture.id,
              },
            },
          };

        merge(draft.entries, memberWithPicture);
        break;
      case EThreadsActionType.GET_SUCCESS:
        const { entities } = action.payload;

        const memberWithMainPicture = entities.threads
          ? Object.values(entities.threads).map(item => ({
              [item.relationships.accountId]: {
                id: item.relationships.accountId,
                attributes: {
                  mainPicture: item.relationships.threadMainPicture,
                },
              },
            }))
          : [];

        merge(draft.entries, entities.members, ...memberWithMainPicture);
        break;
      case EThreadActionType.NEW_MESSAGE:
        merge(draft.entries, {
          [action.payload.relationships.accountId]: {
            id: action.payload.relationships.accountId,
            attributes: {
              mainPicture: get(
                action.payload,
                'relationships.__threadMainPicture'
              ),
            },
          },
        });
        break;
      case EMatchesActionType.GET_AFFINY_MATCH_LIST_SUCCESS:
      case EMatchesActionType.GET_AFFINY_ADDITIONAL_MATCH_LIST_SUCCESS:
      case EMatchesActionType.GET_SORT_MATCH_LIST_SUCCESS:
        merge(
          draft.entries,
          action.payload.entities.members,
          action.payload.entities.memberMatchInfo
        );
        break;
      case EVisitsActionType.GET_VISITS_SUCCESS:
      case EFavoritesActionType.GET_RECEIVED_FAVORITES_SUCCESS:
      case EFavoritesActionType.GET_SENT_FAVORITES_SUCCESS:
        merge(
          draft.entries,
          action.payload.entities.members,
          action.payload.entities.memberInteractionInfo
        );
        break;
      case EPhotoRequestsActionType.GET_SUCCESS:
        merge(draft.entries, action.payload.entities.members);
        break;
      case EPhotoRevealsActionType.GET_SUCCESS:
        merge(draft.entries, action.payload.entities.members);
        break;
      case EBlacklistActionType.GET_SUCCESS:
        merge(
          draft.entries,
          action.payload.entities.members,
          action.payload.entities.memberBlacklistInfo
        );
        break;
      case EBlacklistActionType.POST_SUCCESS:
        merge(draft.entries, {
          [action.payload.receiverId]: {
            attributes: {
              isBlacklisted: true,
            },
            profile: {
              isFavorite: false,
            },
          },
        });
        break;
      case EPhotoRequestsActionType.POST_SUCCESS:
        merge(draft.entries, {
          [action.payload.receiverId]: {
            profile: {
              photoRevealStatus: EPhotoRevealStatus.PENDING,
            },
          },
        });
        break;
      case EBlacklistActionType.DELETE_SUCCESS:
        merge(draft.entries, {
          [action.payload.receiverId]: {
            attributes: {
              isBlacklisted: false,
            },
          },
        });
        break;

      case EScoreActionType.GET_SUCCESS:
        const { againstAboid, ...rest } = action.payload;

        merge(draft.entries, {
          [againstAboid]: {
            attributes: {
              ...rest,
            },
          },
        });
        break;
      case EMembersProfileActionType.GET_COMPATIBILITY_REPORT_LIGHT_SUCCESS:
        const { accountId } = action.payload;

        merge(draft.entries, {
          [accountId]: {
            attributes: {
              ...action.payload.attributes,
            },
          },
        });
        break;
      case EFavoritesActionType.POST_FAVORITE_SUCCESS:
        merge(draft.entries, {
          [action.payload.accountId]: {
            profile: { isFavorite: action.payload.ratingStatus === 'yes' },
          },
        });
        break;
      case EFavoritesActionType.POST_FAVORITE_ERROR:
        merge(draft.entries, {
          [action.payload.accountId]: {
            profile: { isFavorite: false },
          },
        });
        break;
      case EMembersProfileActionType.GET_SUCCESS:
        merge(draft.entries, action.payload);
        break;
    }
  });
